import { Col, Form, Input, Row } from "antd";
import modal from "antd/es/modal";
import { useForm } from "antd/lib/form/Form";
import _ from "lodash";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DraggableModal } from "../../../components/DraggableModal";
import { MapAddSingleMarker } from "../../../components/maps/MapAddPoint";
import { SelectFarmStatus, SelectOwner } from "../../../components/selects";
import { EntBreedingFarm } from "../../../models/breedingFarm";
import { GeoJsonPoint, Geometry } from "../../../models/geoJsonPoint";
import { EntAnimalCount } from "../../../models/handBooks/animalCount";
import AddAnimals from "./AddAnimals";

enum FormField {
    nameRu,
    nameKy,
    farmsStatusId,
    animalBreedIds,
    region,
    areaId,
    ownerId,
    address,
}

interface IProps {
    show: boolean;
    breedingFarm: EntBreedingFarm;
    onHide: () => void;
    onSubmit: (pos: EntBreedingFarm) => Promise<boolean>;
}

export const AddBreedingFarmModal = (props: IProps) => {
    const { t } = useTranslation();
    const [form] = useForm();

    const [loading, setLoading] = useState<boolean>(false); 
    const [geoJsonData, setGeoJsonData] = useState<GeoJsonPoint | null>(null);
    const [animalData, setAnimalData] = useState<EntAnimalCount | null>(null);
    const [regionId, setRegionId] = useState<number | null>(null);
    
    useEffect(() => {
        if (props.show) setFieldsFromEntity();
    }, [props.show]);
    const setFieldsFromEntity = (): void => {
        const animalBreedIdsList = props.breedingFarm.animalBreeds
            ? props.breedingFarm.animalBreeds.map(item => item.id) : [];

        form.setFields([
            { name: FormField.nameKy, value: props.breedingFarm.nameKy },
            { name: FormField.nameRu, value: props.breedingFarm.nameRu },
            { name: FormField.farmsStatusId, value: props.breedingFarm.farmsStatus?.id },
            { name: FormField.animalBreedIds, value: animalBreedIdsList },
            { name: FormField.areaId, value: props.breedingFarm.area?.id },
            { name: FormField.ownerId, value: props.breedingFarm.owner?.id },
            { name: FormField.address, value: props.breedingFarm.address },
        ]);

    }


    const updateGeoJsonPoint = () => {
        const latitude = props.breedingFarm.latitude;
        const longitude = props.breedingFarm.longitude;
      
        if (latitude !== undefined && longitude !== undefined) {
          const geoJsonPoint = new GeoJsonPoint();
          geoJsonPoint.geometry = new Geometry();
          geoJsonPoint.geometry.coordinates = [longitude, latitude]; 
          setGeoJsonData(geoJsonPoint);
        } else {
          console.error( t('breeding_farms.error_geometry') );
        }
      };
      useEffect(() => {
        if (props.breedingFarm.latitude && props.breedingFarm.longitude) {
          updateGeoJsonPoint();
        } else {
            setGeoJsonData(null)
        }
      }, [props.breedingFarm.latitude, props.breedingFarm.longitude]);
    const setEntityFromFields = (): EntBreedingFarm => {
        let breedingFarm = _.cloneDeep(props.breedingFarm);
        breedingFarm.nameKy = form.getFieldValue(FormField.nameKy);
        breedingFarm.nameRu = form.getFieldValue(FormField.nameRu);
        breedingFarm.farmsStatusId = form.getFieldValue(FormField.farmsStatusId);
        breedingFarm.areaId = form.getFieldValue(FormField.areaId);
        breedingFarm.ownerId = form.getFieldValue(FormField.ownerId);
        breedingFarm.address = form.getFieldValue(FormField.address);
        breedingFarm.address = form.getFieldValue(FormField.address);
        breedingFarm.animalBreedIds = form.getFieldValue(FormField.animalBreedIds) || [];
        if (geoJsonData !== null ) {
            breedingFarm.latitude = geoJsonData.geometry?.coordinates[1] as  number ;
            breedingFarm.longitude = geoJsonData.geometry?.coordinates[0] as  number ;
        }
      
        return breedingFarm;
    }

    const hideModal = () => {
        props.onHide();
        setRegionId(null)
    }

    const handleSubmit = async () => {
        setLoading(true);
        try {
            await form.validateFields();
            if(geoJsonData  !== null ){
                const data = setEntityFromFields();
                const result = await props.onSubmit(data);
                if (result) hideModal();
            }else{
                modal.error({ title: t('msg.error'), content: t('breeding_farms.choose_location') });
            }
         
        
        } finally {
            setLoading(false);
        }
    }
    return (
        <>
            <DraggableModal
                title={props.breedingFarm.id === 0 ?  t('breeding_farms.add_phif') : t('breeding_farms.edit_phif')}
                visible={props.show}
                onCancel={() => { hideModal() }}
                onSubmit={handleSubmit}
                submitLoading={loading}
                width={1000}
            >
                <Form
                    initialValues={{ remember: true }}
                    autoComplete="off"
                    layout="vertical"
                    form={form}

                >
                    <Row style={{ margin: 0 }} gutter={16}>
                        <Col className="gutter-row" span={9}>
                            <Form.Item
                                label={ t('general.nameKy') }
                                name={FormField.nameKy}
                                rules={[{ required: true, message: t("msg.fill_in_the_field") }]}
                            >
                                <Input autoComplete="off" />
                            </Form.Item>
                            <Form.Item
                                label={ t('general.nameRu') }
                                name={FormField.nameRu}
                                rules={[{ required: true, message: t("msg.fill_in_the_field") }]}
                            >
                                <Input autoComplete="off" />
                            </Form.Item>
                            <Form.Item
                                label={ t('breeding_farms.farm_status') }
                                name={FormField.farmsStatusId}
                                rules={[{ required: true, message: t("msg.fill_in_the_field") }]}
                            >
                                <SelectFarmStatus />
                            </Form.Item>
                            <Form.Item
                                label={ t('breeding_farms.owner') }
                                name={FormField.ownerId}
                                rules={[{ required: true, message: t("msg.fill_in_the_field") }]}
                            >
                                <SelectOwner />
                            </Form.Item>
                        </Col>
                        <Col className="gutter-row" span={15}>
                            <MapAddSingleMarker
                                setGeoJsonData={setGeoJsonData}
                                geoJsonData={geoJsonData}
                            />
                            <Form.Item
                                style={{ marginTop: 23 }}
                                label={ t('mechanisation.total_account.address') }
                                name={FormField.address}
                                rules={[{ required: true, message: t("msg.fill_in_the_field") }]}
                            >
                                <Input autoComplete="off" />
                            </Form.Item>

                            <div>
                         <AddAnimals setAnimalData={setAnimalData}/>
                         </div>
                        </Col>
                    </Row>
                </Form>
            </DraggableModal>
        </>
    )
}
